import { apiClient } from '@/core/api/client';
import type {
  Payment,
  PaymentFilters,
  PaymentUpdateRequest,
  PaymentValidationRequest,
  PaymentValidation,
  PaymentAcceptanceRequest,
  SmartSplitResponse,
  ValidateAmountResponse,
  AutoDetectResponse,
  PaymentSyncStatus,
  AffairePaymentStatsEnhanced
} from '../types';

export const paymentApi = {
  // Fetch all payments with optional filters
  fetchPayments: async (filters?: PaymentFilters): Promise<Payment[]> => {
    const params = new URLSearchParams();
    
    if (filters) {
      if (filters.status !== 'all') {
        params.append('status', filters.status);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
      if (filters.dateFrom) {
        params.append('date_from', filters.dateFrom);
      }
      if (filters.dateTo) {
        params.append('date_to', filters.dateTo);
      }
    }
    
    const response = await apiClient.get<{ success: boolean; data: Payment[] }>(
      `/api/admin/v2/payments?${params.toString()}`
    );
    
    return response.data;
  },

  // Update payment with affaire_id (using V2 for single affaire)
  updatePaymentAffaire: async (data: PaymentUpdateRequest): Promise<Payment> => {
    // Use V2 endpoint with single affaire allocation
    const response = await apiClient.post<any>(`/api/admin/v2/payments/${data.payment_id}/allocate`, {
      allocations: [{
        affaire_id: data.affaire_id,
        amount: 'full' // Allocate full amount to single affaire
      }]
    });
    // Transform to match expected Payment type
    return {
      ...response,
      affaire_id: data.affaire_id
    };
  },

  // Validate a payment (using V2)
  validatePayment: async (data: PaymentValidationRequest): Promise<PaymentValidation> => {
    return apiClient.post<PaymentValidation>(`/api/admin/v2/payments/${data.payment_id}/validate`, {
      validated: data.validated
    });
  },

  // ===== Multi-Affaire & Smart Allocation APIs =====

  // Smart split payment based on affaire expected amounts
  smartSplitPayment: async (paymentId: number, affaireIds: string[]): Promise<SmartSplitResponse> => {
    return apiClient.post<SmartSplitResponse>(`/api/admin/v2/payments/${paymentId}/smart-split`, {
      affaire_ids: affaireIds
    });
  },

  // Validate payment amount against expected affaire amounts
  validatePaymentAmount: async (paymentId: number, affaireIds: string[]): Promise<ValidateAmountResponse> => {
    return apiClient.post<ValidateAmountResponse>(`/api/admin/v2/payments/${paymentId}/validate-amount`, {
      affaire_ids: affaireIds
    });
  },

  // Auto-detect affaires and suggest smart allocation
  autoDetectAffaires: async (paymentId: number): Promise<AutoDetectResponse> => {
    return apiClient.post<AutoDetectResponse>(`/api/admin/v2/payments/${paymentId}/auto-detect`);
  },


  // Ignore a payment (mark as not part of business)
  ignorePayment: async (paymentId: number, reason?: string): Promise<any> => {
    return apiClient.post(`/api/admin/v2/payments/${paymentId}/ignore`, {
      reason
    });
  },

  // Unignore a payment (restore from ignored status)
  unignorePayment: async (paymentId: number): Promise<any> => {
    return apiClient.delete(`/api/admin/v2/payments/${paymentId}/ignore`);
  },

  // Accept an over/under payment with a note
  acceptPayment: async (data: PaymentAcceptanceRequest): Promise<any> => {
    return apiClient.post(`/api/admin/v2/payments/${data.payment_id}/accept`, {
      affaire_id: data.affaire_id,
      acceptance_note: data.acceptance_note
    });
  },

  // Unaccept a payment (remove acceptance)
  unacceptPayment: async (paymentId: number, affaireId: string): Promise<any> => {
    return apiClient.delete(`/api/admin/v2/payments/${paymentId}/accept/${affaireId}`);
  },

  // Get unpaid payment count for navigation badge
  getUnpaidCount: async (): Promise<{ success: boolean; data: { unpaid_count: number } }> => {
    return apiClient.get<{ success: boolean; data: { unpaid_count: number } }>(
      `/api/admin/v2/payments/unpaid/count`
    );
  },

  // Get banking sync status (last sync timestamp and next scheduled sync)
  getSyncStatus: async (): Promise<PaymentSyncStatus> => {
    return apiClient.get<PaymentSyncStatus>(
      `/api/admin/v2/payments/sync-status`
    );
  },

  // Get affaire payment stats (for multi-payment indicator with payment history)
  getAffairePaymentStats: async (): Promise<{
    success: boolean;
    data: Record<string, AffairePaymentStatsEnhanced>;
  }> => {
    return apiClient.get<{
      success: boolean;
      data: Record<string, AffairePaymentStatsEnhanced>;
    }>(`/api/admin/v2/payments/affaire-stats`);
  }
};